import React, { isValidElement, useCallback, useEffect, useRef } from 'react'
import { ImageList, Stack } from '@mui/material'
import { useListContext, Identifier, BulkActionsToolbar, BulkDeleteButton } from 'react-admin'
import { ImageGridBodyProps, HandleToggleItem } from './type'
import union from 'lodash/union'
import difference from 'lodash/difference'
import { ImageGridItem } from './ImageGridItem'

const defaultBulkActionButtons = <BulkDeleteButton />

export const ImageGridBody: React.FC<ImageGridBodyProps> = ({
  children: fieldComponent,
  rowClick = 'edit',
  sx = {},
  isRowSelectable,
  bulkActionButtons = defaultBulkActionButtons,
  empty,
  ...props
}) => {
  const { data, resource, onSelect, onToggleItem, selectedIds, total } = useListContext(props)
  const lastSelected = useRef(null)
  const hasBulkActions = !!bulkActionButtons !== false

  useEffect(() => {
    if (!selectedIds || selectedIds.length === 0) {
      lastSelected.current = null
    }
  }, [selectedIds])

  // we manage row selection at the datagrid level to allow shift+click to select an array of rows
  const handleToggleItem: HandleToggleItem = useCallback(
    (id, event) => {
      const ids = data.map((record) => record.id)
      const lastSelectedIndex = ids.indexOf(lastSelected.current)
      lastSelected.current = (event.target as HTMLInputElement).checked ? id : null

      if (event.shiftKey && lastSelectedIndex !== -1) {
        const index = ids.indexOf(id)
        const idsBetweenSelections = ids.slice(
          Math.min(lastSelectedIndex, index),
          Math.max(lastSelectedIndex, index) + 1,
        )

        const newSelectedIds = (event.target as HTMLInputElement).checked
          ? union(selectedIds, idsBetweenSelections)
          : difference(selectedIds, idsBetweenSelections)

        onSelect(
          isRowSelectable
            ? newSelectedIds.filter((id: Identifier) =>
                isRowSelectable(data.find((record) => record.id === id)),
              )
            : newSelectedIds,
        )
      } else {
        onToggleItem(id)
      }
    },
    [data, isRowSelectable, onSelect, onToggleItem, selectedIds],
  )

  if (!data || data.length === 0 || total === 0) {
    if (empty) {
      return empty
    }

    return null
  }

  return (
    <Stack>
      {bulkActionButtons !== false ? (
        <BulkActionsToolbar selectedIds={selectedIds}>
          {isValidElement(bulkActionButtons) ? bulkActionButtons : defaultBulkActionButtons}
        </BulkActionsToolbar>
      ) : null}
      <ImageList sx={{ m: 0, ...sx }} {...props}>
        {data.map((record, indexItem) => {
          return (
            <ImageGridItem
              key={record.id ?? `row${indexItem}`}
              record={record}
              hasBulkActions={hasBulkActions && !!selectedIds}
              id={record.id ?? `row${indexItem}`}
              onToggleItem={handleToggleItem}
              resource={resource}
              rowClick={rowClick}
              selectable={!isRowSelectable || isRowSelectable(record)}
              selected={selectedIds?.includes(record.id)}
            >
              {fieldComponent}
            </ImageGridItem>
          )
        })}
      </ImageList>
    </Stack>
  )
}
